/**
 * Chart colour literals for the Liquiditätsplanung surface — **and the
 * compositing parameters they are measured with**.
 *
 * These are SVG *attribute* values — recharts sets ``stroke``/``fill`` as
 * attributes, where ``var(--token)`` does not resolve — so the raw hex has to
 * live in JS rather than in CSS. Deliberately app-local and NOT a design-system
 * token, the same call ``accounts.ts`` makes for ``ACCOUNT_COLORS``.
 *
 * WHY THE HATCH GEOMETRY LIVES HERE TOO (#2986). It used to sit in
 * ``BalanceChart.tsx`` beside the ``<pattern>`` that draws it, which read as the
 * tidier split — colour here, geometry there. #2986 is what that split cost: the
 * three CVD/contrast figures below were measured on the flat literals while the
 * chart painted the planned half of every bar through the pattern at a fraction
 * of that strength, and nothing in either file could notice. A hex and the alpha
 * it is rendered at are ONE claim, so they are pinned together, by one guard, in
 * ``__tests__/chartColors.test.ts``.
 */

/**
 * The bold total/Ist line colour, shared by the chart and the "Alle Konten —
 * Summe" switcher row (#2160) so a single literal drives both.
 *
 * Ex-``SCENARIO_BLUE`` — renamed in #2684 when the weather/economy scenarios
 * were retired and the constant outlived the module it was declared in.
 */
export const TOTAL_LINE_BLUE = '#2563eb';

/**
 * The ``Säulen`` presentation's Einnahmen/Ausgaben pair (#2684 Part B, re-hexed
 * in #2955 §2).
 *
 * CHART-LOCAL VALUES, DELIBERATELY OFF THE DESIGN-SYSTEM SCALE. Until #2955 these
 * were ``#4caf50`` / ``#ef5350``, byte-identical to ``--color-success-400`` /
 * ``--color-error-400``, and this comment told the next reader to keep the two in
 * sync by hand. That is no longer true and must not be re-asserted: neither
 * ``#2e7d32`` nor ``#d16a6a`` exists anywhere in ``design-system/src/tokens/``
 * (the success ramp tops out at ``700 #15803d``, error at ``700 #c62828``). The
 * mirror property was traded away on purpose, because every token-aligned
 * alternative measured worse — ``#2d8a5d``/``#d16a6a`` collapses to ΔE 2,9 and
 * ``#2e7d32``/``#c62828`` to 4,2, both hard FAILs.
 *
 * The near miss is worth naming, because it has a cost that is NOT visible in
 * the validator: ``#15803d`` (``--color-success-700``) reaches ΔE 6,1, still
 * inside the legal floor band, and it is ALREADY rendered on this very page as
 * ``VerdictLine``'s ``--verdict-accent``. Choosing ``#2e7d32`` therefore buys
 * 1,3 ΔE at the price of two greens 1,5 ΔE apart both meaning "positive" on one
 * screen. #2955's kickoff took that trade deliberately, on the CVD numbers. If
 * the page-consistency cost ever outweighs them, ``#15803d`` is the
 * pre-validated fallback — re-run the validator, do not eyeball it.
 *
 * WHY THEY CHANGED. The retired pair separated at **deutan ΔE 1,2** — for a
 * deuteranope, the same colour. It survived only because one bar went up and the
 * other went down, so SIGN carried the distinction that hue did not. #2955 pairs
 * both bars upward on a shared baseline and removes the sign, so the palette had
 * to carry the separation itself. It now does, on three independent axes:
 *
 *   - hue, for dichromats:  ΔE 1,2 → **7,4** (protan; deutan is looser at 12,9)
 *   - tone, for greyscale print:  luma 135/129 → **93/137**, a 44-step gap where
 *     the old pair had 5
 *   - contrast vs white:  2,78/3,49 → **5,13/3,51**, so both fills now clear
 *     WCAG 1.4.11's 3:1 where the green used to sit under it
 *
 * ALL THREE FIGURES HOLD FOR THE HATCHED (planned) SEGMENT TOO, SINCE #2986 —
 * and getting there is the reason the hatch constants below sit in this file.
 * Until #2986 the planned half of a bar was painted through ``ForecastHatch``
 * with the Bar's ``fillOpacity`` at 0,55, and that attribute multiplies the WHOLE
 * pattern paint: the stripe reached the screen at an effective 0,55 and the wash
 * behind it at 0,55 × 0,25 = 0,1375. None of the three figures survived it —
 * over white the stripes measured CVD ΔE 4,26 at 2,26/1,92 contrast, the wash
 * ΔE 1,08 — so the pair #2955 bought was, for the projected half of the chart,
 * still the pair #2955 replaced (which measured 1,94 and 0,66 in the same place).
 *
 * THE FIX IS GEOMETRIC, AND ALPHA ALONE PROVABLY COULD NOT BE IT. Sweeping the
 * opacity upward does reach the floor: at α ≈ 0,80 the composite hits ΔE 6,17.
 * But a planned segment is stacked DIRECTLY against its own solid Ist segment,
 * so "this part is a projection" is itself a two-colour distinction — and at
 * α 0,80 the plan fill sits only ΔE 7,35 from the Ist fill it abuts, under the
 * same normal-vision floor (15) this palette must clear against any neighbour.
 * Raising alpha buys the Einnahmen/Ausgaben pair by spending the Ist/Prognose
 * one. Both numbers are pinned in ``__tests__/chartColors.test.ts``, so this is
 * arithmetic rather than an aesthetic preference.
 *
 * What ships instead is two changes to the pattern's geometry, neither of which
 * touches a hex:
 *
 *   1. THE FADE MOVED OFF THE INK. There is no ``fillOpacity`` on the Bar any
 *      more; the reduced alpha lives on the pattern's wash rect alone
 *      (``HATCH_WASH_OPACITY``). The stroke is therefore laid down at full
 *      strength, so a planned segment's INK is the literal — which is what makes
 *      the three figures above apply to it unchanged. The ink reads ~1,8× stronger
 *      than before at the SAME coverage, so #2955 §3's 11 %-ink texture argument
 *      is untouched: strength changed, area did not.
 *   2. THE ANGLE IS NOW PER SIDE (``HATCH_ANGLE_DEG``) — ``/`` for Einnahmen,
 *      ``\`` for Ausgaben. The wash is 8/9 of the segment's area and no alpha
 *      that still reads as a projection can lift it past the floor (even the
 *      area-weighted average of ink + wash only reaches ΔE 2,45), so the wash is
 *      carried by a cue that is not colour at all. Two mirrored directions are
 *      90° apart, the most two line directions can be, and no colour transform
 *      can take that away from a dichromat.
 *
 * That is a visible design change, deliberately: planned segments now read as
 * crisp thin diagonals on a pale wash rather than as a uniformly faded solid,
 * and the two sides lean opposite ways. It also makes the two legs of an internal
 * transfer distinguishable, which they never were.
 *
 * The 7,4 sits in the validator's 6–8 "floor band", which is legal ONLY with
 * secondary encoding. THREE cues now separate the PAIR: a fixed within-pair order
 * (Einnahmen always left), an always-present DOM legend, and the per-side hatch
 * angle on the planned segments. Before #2986 the hatch was NOT one of them —
 * ``ForecastHatch`` drew identical geometry for income, expense and transfer
 * alike, varying only ``color``, so it separated Ist from Prognose and never
 * Einnahmen from Ausgaben.
 *
 * These numbers are pinned by ``__tests__/chartColors.test.ts``. If a future
 * "let's calm it down" reds that file, re-run the validator and argue the new
 * numbers — do not re-baseline the pins. Desaturating is the specific trap: it
 * walks EXPENSE_RED toward ``TRANSFER_GREY`` below, and the softest-looking
 * candidates break the NORMAL-vision floor, i.e. for readers with no deficiency
 * at all.
 *
 * They stay raw hex rather than ``var(--token)`` for the reason the whole file
 * exists: recharts writes ``fill`` as an SVG *attribute*, where
 * ``fill="var(--color-success-400)"`` does not resolve and the bar renders black.
 */
export const INCOME_GREEN = '#2e7d32';
export const EXPENSE_RED = '#d16a6a';

/**
 * The ``transfer_internal`` sub-segment inside each Säule (#2743) — own money
 * moved between the farmer's OWN accounts, which is real bank movement (so it
 * stays in the bar) but is neither revenue nor cost (so it must not read as
 * either). On WF that is 30,6 % of every Einnahmen bar and 28,5 % of every
 * Ausgaben bar.
 *
 * ACHROMATIC on purpose, and #2955 did NOT change it. The original argument was
 * that the two series colours already collided in greyscale (``#4caf50`` and
 * ``#ef5350`` were luma 135 and 129 — told apart by SIGN, not by print tone), so
 * a third *hue* would be the one entry a red-green-deficient reader could not
 * place. #2955 fixed the premise — the pair above now separates on hue AND tone
 * without the sign — but the conclusion survives it unchanged, and for the
 * stronger reason: this segment is a NEUTRAL by meaning, not merely a third
 * category. Own money moved between the farmer's own accounts is neither
 * revenue nor cost, so giving it a hue would assert a category it does not
 * belong to. ``#9ca3af`` is luma 162, a clear step above both 93 and 137, and it
 * is the design system's own muted value (``--color-gray-500``, what
 * ``--color-text-tertiary`` resolves to). Note that it is now the ONLY constant
 * here that still tracks a token — the pair above deliberately no longer does.
 *
 * Deliberately low-salience: the segment's message is "this part is NOT
 * turnover", so it must recede against the series colour while staying legible
 * against the plot background. Contrast vs white is 2,54:1 — under WCAG 1.4.11's
 * 3:1 for a meaningful graphical object. Since #2955 this is the palette's ONE
 * exception rather than its general posture (the pair now clears 3:1 at 5,13 and
 * 3,51, where the old green sat at 2,78). It is still the right trade: the
 * segment's real boundary cue is its shared edge with the saturated series
 * colour, not its edge against the background, and raising its contrast would
 * make a non-turnover sub-segment compete with the turnover it sits inside.
 * What DOES have to hold is separation from the two colours it touches, and it
 * does — worst-case CVD ΔE 20,1 against the green and 9,7 against the red, both
 * above the 8,0 target, with the normal-vision floor pinned in
 * ``__tests__/chartColors.test.ts``.
 */
export const TRANSFER_GREY = '#9ca3af'; /* --color-gray-500 */

/*
 * ``ZERO_BASELINE_GREY = '#6b7280'`` STOOD HERE UNTIL #2955 §2, and it is worth
 * one paragraph why it is gone rather than merely unused.
 *
 * It stroked the ``<ReferenceLine y={0}>`` the two Säulen used to diverge
 * around, one step darker than ``TRANSFER_GREY`` so a ~100 % own-account month
 * could not swallow the rule with its own grey segment (#2743). #2955's palette
 * subtask then landed ``INCOME_GREEN = #2e7d32``, which sits at **1,06:1**
 * against that grey (the retired ``#4caf50`` was 1,74:1) — and the Einnahmen bar
 * started exactly ON the rule, so where the two met the rule became invisible:
 * precisely the failure the constant existed to prevent. That regression was
 * accepted at kickoff, on the explicit condition that this subtask delete the
 * rule outright. It does: both bars now rise from a shared baseline under a
 * ``[0, dataMax]`` y-domain, so the AXIS FLOOR is the zero line and a separate
 * rule has nothing left to mark.
 *
 * Re-introducing a zero-baseline colour would therefore resurrect a live
 * contrast defect, and ``noUnusedLocals`` cannot catch an unused *export*.
 * ``__tests__/chartColors.test.ts`` asserts the absence for that reason.
 */

/*
 * ``FORECAST_FILL_OPACITY = 0.55`` STOOD HERE UNTIL #2986, and its absence is a
 * gate rather than housekeeping — the same situation as ``ZERO_BASELINE_GREY``
 * above, and guarded the same way.
 *
 * It was passed as the planned Bars' ``fillOpacity``. That attribute scales the
 * whole paint operation, so with a ``<pattern>`` fill it multiplied the hatch's
 * STROKE as well as its wash — which is the entire mechanism of #2986. Any future
 * "let's fade the projection a bit more" that reaches for a Bar-level opacity
 * re-creates the defect exactly, and ``noUnusedLocals`` cannot catch an unused
 * *export*, so ``__tests__/chartColors.test.ts`` asserts that no constant
 * matching ``FILL_OPACITY`` is exported from this module at all. Fade the wash
 * (``HATCH_WASH_OPACITY``) instead; the ink must stay unattenuated.
 */

/**
 * The Prognose hatch's geometry, in one place because THREE renderers read it:
 * the SVG ``<pattern>`` (``BalanceChart``'s ``ForecastHatch``), the DOM legend's
 * swatch gradient, and the guard in ``__tests__/chartColors.test.ts`` that
 * measures the composite. See the ``INCOME_GREEN`` block above for why these
 * moved out of the component in #2986.
 *
 * RETUNED IN #2955 §3 and deliberately NOT retuned again by #2986. It was a 3 px
 * stroke on a 6 px tile — 50 % ink, which is a STRIPE, not a texture, and it
 * fought the fill it was supposed to qualify. At 1 px on 9 px it is 11 % ink.
 * #2986 makes that ink opaque, so it now reads ~1,8× stronger at the same
 * coverage; widening it on top of that would walk the hatched segment back toward
 * the solid one it has to stay distinct from.
 */
export const HATCH_TILE_PX = 9;
export const HATCH_STROKE_PX = 1;

/**
 * The wash behind the stripes — what keeps a hatched segment recognisably the
 * same colour as the solid one it stacks on, and, since #2986, the ONLY place the
 * "this is a projection" fade is applied. 0,25 leaves the wash ΔE 28–37 from its
 * own solid fill (so the two states stay obviously different) while the ink
 * carries the hue at full strength.
 */
export const HATCH_WASH_OPACITY = 0.25;

/**
 * The hatch's rotation PER SIDE of the pair (#2986) — the pair-separating cue
 * that is not a colour.
 *
 * Mirrored on purpose: ``+45`` and ``−45`` put the two textures 90° apart, the
 * largest angular separation two line directions can have, so the two washes stay
 * told apart by a reader for whom they are the same colour. The transfer neutral
 * has no entry of its own — each of its two legs takes the angle of the SIDE it
 * is drawn in, which is why ``COLUMN_HATCH_IDS`` needs one pattern per (colour,
 * side) rather than one per colour. The angle means "which side of the pair",
 * never "which category".
 *
 * SVG SEMANTICS, not CSS: ``patternTransform="rotate(45)"`` rotates the PATTERN,
 * so the pattern's vertical line ends up running bottom-left→top-right (``/``).
 * A CSS gradient angle names the gradient LINE and its bands run perpendicular to
 * it, so the legend swatch spells the same ``/`` as ``135deg`` — see
 * ``hatchSwatchBackground``.
 */
export const HATCH_ANGLE_DEG = { income: 45, expense: -45 } as const;
